import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

function InfrastructureMonitor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchInfrastructureStatus = useCallback(async () => {
    try {
      const response = await adminAPI.getInfrastructureStatus();
      setData(response.data.data);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch infrastructure status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInfrastructureStatus();

    // Auto-refresh every 30 seconds
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchInfrastructureStatus, 30000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchInfrastructureStatus, autoRefresh]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
      case 'connected':
        return '#10b981';
      case 'degraded':
        return '#f59e0b';
      case 'unhealthy':
      case 'disconnected':
      case 'critical':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
      case 'connected':
        return '✅';
      case 'degraded':
        return '⚠️';
      case 'unhealthy':
      case 'disconnected':
      case 'critical':
        return '❌';
      default:
        return '❓';
    }
  };

  if (loading) {
    return (
      <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', minHeight: '100vh' }}>
        <Navbar />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <div style={{ color: 'white', fontSize: '24px' }}>🔄 Loading infrastructure status...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container" style={{ paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-2xl)' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }}>
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            color: 'white',
            marginBottom: 'var(--space-sm)'
          }}>
            🖥️ Infrastructure Monitor
          </h1>
          <p style={{ color: '#94a3b8', fontSize: 'var(--font-size-lg)' }}>
            Real-time monitoring of server health and load balancing
          </p>
          <div style={{ marginTop: 'var(--space-md)', display: 'flex', justifyContent: 'center', gap: 'var(--space-md)', alignItems: 'center' }}>
            <button
              onClick={fetchInfrastructureStatus}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--primary)',
                color: 'white',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              🔄 Refresh Now
            </button>
            <label style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh (30s)
            </label>
            {lastRefresh && (
              <span style={{ color: '#64748b', fontSize: '14px' }}>
                Last updated: {lastRefresh.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid #ef4444',
            borderRadius: '12px',
            padding: 'var(--space-md)',
            marginBottom: 'var(--space-xl)',
            color: '#fca5a5'
          }}>
            ⚠️ Error: {error}
          </div>
        )}

        {/* Overall Status Banner */}
        {data && (
          <div style={{
            background: `linear-gradient(135deg, ${getStatusColor(data.overallStatus)}33 0%, ${getStatusColor(data.overallStatus)}11 100%)`,
            border: `2px solid ${getStatusColor(data.overallStatus)}`,
            borderRadius: '16px',
            padding: 'var(--space-xl)',
            marginBottom: 'var(--space-xl)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: 'var(--space-sm)' }}>
              {data.overallStatus === 'healthy' ? '🟢' : data.overallStatus === 'degraded' ? '🟡' : '🔴'}
            </div>
            <h2 style={{ color: 'white', fontSize: 'var(--font-size-2xl)', marginBottom: 'var(--space-xs)' }}>
              System Status: {data.overallStatus.toUpperCase()}
            </h2>
            <p style={{ color: '#94a3b8' }}>
              {data.summary.healthyInstances}/{data.summary.totalInstances} instances healthy
            </p>
          </div>
        )}

        {/* Architecture Diagram */}
        {data && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '16px',
            padding: 'var(--space-xl)',
            marginBottom: 'var(--space-xl)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h3 style={{ color: 'white', marginBottom: 'var(--space-lg)', textAlign: 'center' }}>
              🏗️ Architecture Overview
            </h3>

            {/* Visual Architecture */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--space-lg)'
            }}>
              {/* Users */}
              <div style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                padding: '12px 24px',
                borderRadius: '12px',
                color: 'white',
                fontWeight: '600'
              }}>
                👥 Users
              </div>

              <div style={{ color: '#64748b', fontSize: '24px' }}>↓</div>

              {/* Route 53 */}
              <div style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                padding: '16px 32px',
                borderRadius: '12px',
                color: 'white',
                textAlign: 'center'
              }}>
                <div style={{ fontWeight: '700', marginBottom: '4px' }}>🌐 Route 53 DNS</div>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>
                  Weighted {data.routing.weight} • Health Checks every {data.routing.healthCheckInterval}s
                </div>
              </div>

              <div style={{ color: '#64748b', fontSize: '24px' }}>↓</div>

              {/* Instances */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: 'var(--space-lg)',
                width: '100%',
                maxWidth: '700px'
              }}>
                {data.instances.map((instance, index) => (
                  <div
                    key={instance.id}
                    style={{
                      background: instance.health.status === 'healthy'
                        ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)'
                        : 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0.1) 100%)',
                      border: `2px solid ${getStatusColor(instance.health.status)}`,
                      borderRadius: '16px',
                      padding: 'var(--space-lg)',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: '32px', marginBottom: 'var(--space-sm)' }}>
                      {instance.health.status === 'healthy' ? '🖥️' : '💀'}
                    </div>
                    <h4 style={{ color: 'white', marginBottom: '8px' }}>
                      {instance.name}
                    </h4>
                    <div style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      background: getStatusColor(instance.health.status),
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: '600',
                      marginBottom: '12px'
                    }}>
                      {getStatusIcon(instance.health.status)} {instance.health.status.toUpperCase()}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '4px' }}>
                      <strong>IP:</strong> {instance.ip}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '4px' }}>
                      <strong>AZ:</strong> {instance.availabilityZone}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '4px' }}>
                      <strong>Type:</strong> {instance.type}
                    </div>
                    {instance.health.responseTime && (
                      <div style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '4px' }}>
                        <strong>Response:</strong> {instance.health.responseTime}ms
                      </div>
                    )}
                    {instance.health.uptime && (
                      <div style={{ color: '#94a3b8', fontSize: '14px' }}>
                        <strong>Uptime:</strong> {Math.floor(instance.health.uptime / 60)}m {Math.floor(instance.health.uptime % 60)}s
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ color: '#64748b', fontSize: '24px' }}>↓</div>

              {/* Shared Services */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 'var(--space-md)',
                width: '100%',
                maxWidth: '700px'
              }}>
                {Object.entries(data.sharedServices).map(([key, service]) => (
                  <div
                    key={key}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: `1px solid ${getStatusColor(service.status)}`,
                      borderRadius: '12px',
                      padding: 'var(--space-md)',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>
                      {key === 'mongodb' ? '🍃' : key === 'redis' ? '🔴' : '🔍'}
                    </div>
                    <div style={{ color: 'white', fontWeight: '600', marginBottom: '4px' }}>
                      {service.name}
                    </div>
                    <div style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      background: getStatusColor(service.status),
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      {getStatusIcon(service.status)} {service.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Routing Details */}
        {data && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 'var(--space-lg)',
            marginBottom: 'var(--space-xl)'
          }}>
            {/* Routing Config */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: 'var(--space-xl)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h3 style={{ color: 'white', marginBottom: 'var(--space-lg)' }}>
                🔀 Load Balancing
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>Type:</span>
                  <span style={{ color: 'white', fontWeight: '600' }}>{data.routing.type}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>Weight Distribution:</span>
                  <span style={{ color: 'white', fontWeight: '600' }}>{data.routing.weight}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>Active Instances:</span>
                  <span style={{ color: '#10b981', fontWeight: '600' }}>{data.routing.activeInstances}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>Health Check Interval:</span>
                  <span style={{ color: 'white', fontWeight: '600' }}>{data.routing.healthCheckInterval}s</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>Failover Time:</span>
                  <span style={{ color: '#f59e0b', fontWeight: '600' }}>{data.routing.estimatedFailoverTime}</span>
                </div>
              </div>
            </div>

            {/* Architecture Features */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: 'var(--space-xl)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h3 style={{ color: 'white', marginBottom: 'var(--space-lg)' }}>
                🏛️ Architecture Features
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {Object.entries(data.architecture).map(([key, value]) => (
                  <div key={key} style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                    <span>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</span>
                    <span style={{ color: 'white', fontWeight: '600', textAlign: 'right', maxWidth: '60%' }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Instance Details Table */}
        {data && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '16px',
            padding: 'var(--space-xl)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            overflowX: 'auto'
          }}>
            <h3 style={{ color: 'white', marginBottom: 'var(--space-lg)' }}>
              📊 Instance Details
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Instance</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>IP Address</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>AZ</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Response Time</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Services</th>
                </tr>
              </thead>
              <tbody>
                {data.instances.map((instance) => (
                  <tr key={instance.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <td style={{ padding: '12px', color: 'white', fontWeight: '600' }}>
                      {instance.name}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        background: getStatusColor(instance.health.status),
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {getStatusIcon(instance.health.status)} {instance.health.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px', color: '#94a3b8', fontFamily: 'monospace' }}>
                      {instance.ip}
                    </td>
                    <td style={{ padding: '12px', color: '#94a3b8' }}>
                      {instance.availabilityZone}
                    </td>
                    <td style={{ padding: '12px', color: instance.health.responseTime ? '#10b981' : '#ef4444' }}>
                      {instance.health.responseTime ? `${instance.health.responseTime}ms` : 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {instance.health.services && Object.entries(instance.health.services).map(([key, value]) => (
                        <span
                          key={key}
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '8px',
                            background: value === 'connected' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                            color: value === 'connected' ? '#10b981' : '#ef4444',
                            fontSize: '11px',
                            marginRight: '4px',
                            marginBottom: '4px'
                          }}
                        >
                          {key}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Info Box */}
        <div style={{
          background: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '12px',
          padding: 'var(--space-lg)',
          marginTop: 'var(--space-xl)',
          color: '#93c5fd'
        }}>
          <strong>ℹ️ How Failover Works:</strong>
          <ul style={{ margin: '8px 0 0 20px', lineHeight: '1.8' }}>
            <li>Route 53 checks each server's health every 30 seconds</li>
            <li>After 3 consecutive failures (~90 seconds), traffic is automatically rerouted</li>
            <li>When a failed server recovers, it's automatically added back to rotation</li>
            <li>WebSocket connections sync across servers via Redis adapter</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default InfrastructureMonitor;
